/**
 * AutoReplySettingsCard — respuesta automática por WhatsApp (solo admin).
 *
 * Dos interruptores con pesos distintos:
 *   - SHADOW: procesa y deja registrado qué habría respondido. No envía.
 *     Se enciende y se apaga con un clic.
 *   - SEND: envío real. Efectivo solo con el techo del servidor abierto Y
 *     este interruptor encendido. Con el techo cerrado el interruptor no se
 *     puede encender —y la tarjeta lo dice, en vez de aparentar que sí—.
 *     Encenderlo pide una confirmación escrita; apagarlo, nunca: es el
 *     kill switch.
 *   - TODOS LOS LEADS: a quién llega SEND. Apagado, solo a la allowlist de
 *     prueba del servidor; encendido, a todo lead elegible. Misma regla:
 *     encender pide confirmación escrita, apagar no.
 *
 * El estado se relee al volver a la pestaña y cada 15 s: una pantalla vieja
 * que enseñara SEND apagado cuando está encendido convertiría el kill switch
 * en un «encender». Y si un interruptor no se pudo leer, se dice
 * «desconocido» y se ofrece apagarlo directamente, sin adivinar.
 *
 * Nada de esto toca el .env, reinicia el backend ni entra al VPS.
 */
import React, { useCallback, useEffect, useState } from "react";
import { formatError } from "@/lib/api";
import { getAutoReplySettings, setAutoReplyFlag } from "@/lib/autoReplySettingsApi";
import { Lightning, LockSimple, PaperPlaneTilt, Warning } from "@phosphor-icons/react";

const TAP = "min-h-[44px]";
const PALABRA = "ENVIAR";
const REFRESCO_MS = 15000;

const MENSAJES = {
  env_ceiling_closed: "El techo del servidor está cerrado: el envío real no se puede encender desde aquí.",
  confirmation_required: "Encender el envío real necesita confirmación.",
  duplicate_setting: "Hay dos registros para este interruptor. Sigue apagado; se corrige solo en el próximo arranque.",
  concurrent_update: "Otro admin lo estaba cambiando a la vez: revisa el estado y repite si hace falta.",
  settings_unavailable: "No se pudo confirmar el cambio.",
};

function fecha(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return null;
  }
}

function UltimoCambio({ meta, testId }) {
  if (!meta?.updated_at) return null;
  return (
    <div className="text-[11px] text-stone-400 mt-1" data-testid={testId}>
      Último cambio: {fecha(meta.updated_at)}
      {meta.updated_by_name ? ` · ${meta.updated_by_name}` : ""}
    </div>
  );
}

function Interruptor({ checked, disabled, onClick, testId, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      className={`${TAP} shrink-0 inline-flex items-center justify-center px-1 disabled:cursor-not-allowed`}
    >
      <span
        className={`inline-flex items-center h-6 w-11 rounded-full transition-colors ${
          checked ? "bg-[#9C5236]" : "bg-stone-300 dark:bg-stone-700"
        } ${disabled ? "opacity-40" : ""}`}
      >
        <span
          className={`block h-5 w-5 rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-[22px]" : "translate-x-[2px]"
          }`}
        />
      </span>
    </button>
  );
}

function Aviso({ tone, children, testId }) {
  const tonos = {
    error: "text-red-700 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-950/40 dark:border-red-800",
    warn: "text-amber-800 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-800",
    info: "text-stone-700 bg-stone-50 border-stone-200 dark:text-stone-300 dark:bg-stone-900/60 dark:border-stone-700",
  };
  return (
    <div className={`text-xs rounded px-3 py-2 border break-words ${tonos[tone]}`} data-testid={testId}>
      {children}
    </div>
  );
}

function desconocido(meta) {
  return meta?.readable === false;
}

export default function AutoReplySettingsCard() {
  const [estado, setEstado] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aviso, setAviso] = useState(null);
  // null | "send" | "all_leads": qué interruptor está pidiendo confirmación.
  const [confirmando, setConfirmando] = useState(null);
  const [escrito, setEscrito] = useState("");

  const cargar = useCallback(async () => {
    try {
      setEstado(await getAutoReplySettings());
      setError(null);
      return true;
    } catch (e) {
      setError(formatError(e));
      return false;
    }
  }, []);

  useEffect(() => {
    cargar();
    const alVolver = () => {
      if (document.visibilityState !== "hidden") cargar();
    };
    window.addEventListener("focus", alVolver);
    document.addEventListener("visibilitychange", alVolver);
    const id = window.setInterval(() => {
      if (document.visibilityState !== "hidden") cargar();
    }, REFRESCO_MS);
    return () => {
      window.removeEventListener("focus", alVolver);
      document.removeEventListener("visibilitychange", alVolver);
      window.clearInterval(id);
    };
  }, [cargar]);

  const cambiar = async (flag, enabled, opts) => {
    if (busy) return;
    setBusy(true);
    setAviso(null);
    try {
      const r = await setAutoReplyFlag(flag, enabled, opts);
      if (r?.settings) setEstado(r.settings);
      return true;
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      const base = MENSAJES[code] || formatError(e);
      const recargado = await cargar();
      setAviso(`${base} ${recargado ? "He recargado el estado." : "Y no se pudo recargar el estado: revisa antes de repetir."}`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const alternarShadow = () => cambiar("shadow", !estado.shadow_enabled);

  const alternarSend = () => {
    // Estado desconocido o encendido: el clic solo puede APAGAR. Nunca se
    // abre el diálogo de encender sobre algo que quizá ya está enviando.
    if (estado.send_mongo_switch || desconocido(estado.send)) {
      // Apagar es el kill switch: sin diálogo, sin esperar a nada.
      cambiar("send", false);
      return;
    }
    if (!estado.send_env_ceiling) return;
    setEscrito("");
    setConfirmando("send");
  };

  const alternarTodos = () => {
    // Igual que SEND: encendido o desconocido, el clic solo apaga.
    if (estado.send_audience === "all" || desconocido(estado.all_leads)) {
      cambiar("all_leads", false);
      return;
    }
    setEscrito("");
    setConfirmando("all_leads");
  };

  const confirmar = async () => {
    if (escrito.trim().toUpperCase() !== PALABRA || !confirmando) return;
    const ok = await cambiar(confirmando, true, { confirm: true });
    if (ok) setConfirmando(null);
  };

  if (error && !estado) {
    return (
      <div className="dv-card space-y-3" data-testid="auto-reply-settings-card">
        <Aviso tone="error" testId="auto-reply-load-error">No se pudo cargar el estado: {error}</Aviso>
        <button type="button" onClick={cargar} className={`dv-btn dv-btn-ghost text-xs ${TAP}`}>
          Reintentar
        </button>
      </div>
    );
  }
  if (!estado) {
    return <div className="dv-card text-sm text-stone-500" data-testid="auto-reply-settings-card">Cargando…</div>;
  }

  const techo = estado.send_env_ceiling;
  const envioEncendidoSinTecho = !techo && estado.send_mongo_switch;
  const sombraDesconocida = desconocido(estado.shadow);
  const envioDesconocido = desconocido(estado.send);
  const todosDesconocido = desconocido(estado.all_leads);
  const paraTodos = estado.send_audience === "all";

  return (
    <div className="dv-card space-y-5" data-testid="auto-reply-settings-card">
      {estado.sending_now && (
        <div
          className="flex items-center gap-2 text-sm font-medium rounded px-3 py-2 border text-red-800 bg-red-50 border-red-200 dark:text-red-200 dark:bg-red-950/50 dark:border-red-800"
          data-testid="auto-reply-sending-now"
        >
          <PaperPlaneTilt size={16} weight="bold" className="shrink-0" />
          Enviando respuestas reales a clientes.
        </div>
      )}

      {aviso && <Aviso tone="error" testId="auto-reply-notice">{aviso}</Aviso>}
      {error && (
        <Aviso tone="warn" testId="auto-reply-stale">
          No se pudo actualizar el estado ({error}). Lo que ves es el último que se pudo leer.
        </Aviso>
      )}
      {(sombraDesconocida || envioDesconocido) && (
        <Aviso tone="warn" testId="auto-reply-read-problem">
          No se pudo leer algún interruptor, así que su estado real es desconocido. Si hace falta pararlo, usa
          «Apagar»: funciona aunque no se haya podido leer.
        </Aviso>
      )}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={cargar}
          className={`dv-btn dv-btn-ghost text-xs ${TAP}`}
          data-testid="auto-reply-refresh"
        >
          Actualizar
        </button>
      </div>
      {estado.unique_index === false && (
        <Aviso tone="warn" testId="auto-reply-index-missing">
          Falta el índice único de ajustes. Los interruptores siguen fallando cerrado; se crea en el próximo arranque.
        </Aviso>
      )}

      {/* SHADOW */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 font-medium">
            <Lightning size={16} className="shrink-0" />
            Procesamiento (SHADOW)
          </div>
          <p className="text-xs text-stone-500 mt-1">
            Analiza cada WhatsApp entrante y deja registrado qué habría respondido. Por sí solo no envía nada.
          </p>
          <div className="text-xs mt-1" data-testid="auto-reply-shadow-state">
            {sombraDesconocida ? "Desconocido (no se pudo leer)" : estado.shadow_enabled ? "Encendido" : "Apagado"}
          </div>
          <UltimoCambio meta={estado.shadow} testId="auto-reply-shadow-meta" />
          {sombraDesconocida && (
            <button
              type="button"
              onClick={() => cambiar("shadow", false)}
              disabled={busy}
              className={`mt-2 dv-btn text-xs border border-red-300 text-red-700 ${TAP}`}
              data-testid="auto-reply-shadow-kill"
            >
              Apagar procesamiento
            </button>
          )}
        </div>
        <Interruptor
          checked={!!estado.shadow_enabled}
          disabled={busy || sombraDesconocida}
          onClick={alternarShadow}
          testId="auto-reply-shadow-toggle"
          label="Procesamiento en sombra"
        />
      </div>

      <div className="border-t border-stone-100 dark:border-stone-800" />

      {/* SEND */}
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 font-medium">
              <PaperPlaneTilt size={16} className="shrink-0" />
              Envío real (SEND)
            </div>
            <p className="text-xs text-stone-500 mt-1">
              Deja salir al cliente las respuestas que pasan todos los controles. Necesita el techo del servidor abierto y este interruptor encendido.
            </p>
            <div className="text-xs mt-1" data-testid="auto-reply-send-switch-state">
              Interruptor de la plataforma:{" "}
              {envioDesconocido ? "desconocido (no se pudo leer)" : estado.send_mongo_switch ? "encendido" : "apagado"}
            </div>
            <UltimoCambio meta={estado.send} testId="auto-reply-send-meta" />
            {envioDesconocido && (
              <button
                type="button"
                onClick={() => cambiar("send", false)}
                disabled={busy}
                className={`mt-2 dv-btn text-xs bg-red-700 text-white hover:bg-red-800 ${TAP}`}
                data-testid="auto-reply-send-kill"
              >
                Apagar envío
              </button>
            )}
          </div>
          <Interruptor
            checked={!!estado.send_mongo_switch}
            disabled={busy || envioDesconocido || (!techo && !estado.send_mongo_switch)}
            onClick={alternarSend}
            testId="auto-reply-send-toggle"
            label="Envío real"
          />
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 border ${
              techo
                ? "text-emerald-800 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-800"
                : "text-stone-700 bg-stone-100 border-stone-200 dark:text-stone-300 dark:bg-stone-800 dark:border-stone-700"
            }`}
            data-testid="auto-reply-ceiling"
          >
            <LockSimple size={12} weight="bold" />
            Techo del servidor: {techo ? "abierto" : "cerrado"}
          </span>
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 border font-medium ${
              estado.send_effective
                ? "text-red-800 bg-red-50 border-red-200 dark:text-red-200 dark:bg-red-950/50 dark:border-red-800"
                : "text-stone-700 bg-stone-100 border-stone-200 dark:text-stone-300 dark:bg-stone-800 dark:border-stone-700"
            }`}
            data-testid="auto-reply-send-effective"
          >
            Envío efectivo: {envioDesconocido && techo ? "desconocido" : estado.send_effective ? "sí" : "no"}
          </span>
        </div>

        {!techo && (
          <Aviso tone="info" testId="auto-reply-ceiling-closed">
            El techo del servidor está cerrado: desde aquí no se puede enviar nada, aunque este interruptor estuviera
            encendido. Abrirlo es una preparación única en el servidor (variable <code>AUTO_REPLY_SEND_ENABLED</code>);
            después, el envío se enciende y se apaga solo desde aquí.
            {envioEncendidoSinTecho && " El interruptor quedó encendido: apágalo para dejarlo limpio."}
          </Aviso>
        )}
        {estado.send_effective && !estado.shadow_enabled && (
          <Aviso tone="info" testId="auto-reply-send-without-shadow">
            El envío está abierto, pero sin procesamiento (SHADOW apagado) no hay respuestas que enviar.
          </Aviso>
        )}
      </div>

      <div className="border-t border-stone-100 dark:border-stone-800" />

      {/* TODOS LOS LEADS */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="font-medium">Todos los leads elegibles</div>
          <p className="text-xs text-stone-500 mt-1">
            A quién llega el envío real. Apagado, solo a los teléfonos de prueba del servidor; encendido, a cualquier lead
            elegible con el teléfono verificado. Sin el envío real encendido no sale nada.
          </p>
          <div className="text-xs mt-1" data-testid="auto-reply-audience-state">
            {todosDesconocido
              ? "Desconocido (no se pudo leer)"
              : paraTodos
                ? "Todos los leads elegibles"
                : `Solo prueba (${estado.send_allowlist_count ?? 0} teléfono${estado.send_allowlist_count === 1 ? "" : "s"})`}
          </div>
          <UltimoCambio meta={estado.all_leads} testId="auto-reply-audience-meta" />
        </div>
        <Interruptor
          checked={paraTodos}
          disabled={busy}
          onClick={alternarTodos}
          testId="auto-reply-audience-toggle"
          label="Todos los leads elegibles"
        />
      </div>

      {confirmando && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="auto-reply-confirm-title"
          data-testid="auto-reply-send-confirm"
          data-flag={confirmando}
        >
          <div className="dv-card w-full max-w-md space-y-4 bg-white dark:bg-stone-900">
            <div className="flex items-center gap-2 font-medium" id="auto-reply-confirm-title">
              <Warning size={18} weight="bold" className="text-red-700 shrink-0" />
              {confirmando === "all_leads" ? "Responder a todos los leads" : "Encender el envío real"}
            </div>
            <p className="text-sm text-stone-600 dark:text-stone-300">
              {confirmando === "all_leads"
                ? "Las respuestas que pasen todos los controles saldrán a cualquier lead elegible, no solo a los teléfonos de prueba. Puedes apagarlo en cualquier momento desde aquí, sin confirmación."
                : "A partir de ahora las respuestas que pasen todos los controles saldrán a los clientes por WhatsApp, sin que las revise una persona. Puedes apagarlo en cualquier momento desde aquí, sin confirmación."}
            </p>
            <label className="block text-xs text-stone-500">
              Escribe <strong>{PALABRA}</strong> para confirmar
              <input
                className="dv-input mt-1 w-full"
                value={escrito}
                onChange={(e) => setEscrito(e.target.value)}
                autoFocus
                data-testid="auto-reply-send-confirm-input"
              />
            </label>
            {aviso && <Aviso tone="error" testId="auto-reply-confirm-notice">{aviso}</Aviso>}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmando(null)}
                disabled={busy}
                className={`dv-btn dv-btn-ghost text-sm ${TAP}`}
                data-testid="auto-reply-send-confirm-cancel"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmar}
                disabled={busy || escrito.trim().toUpperCase() !== PALABRA}
                className={`dv-btn text-sm bg-red-700 text-white hover:bg-red-800 disabled:opacity-50 ${TAP}`}
                data-testid="auto-reply-send-confirm-yes"
              >
                {confirmando === "all_leads" ? "Responder a todos" : "Encender envío real"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
